/**
 * Service for managing broker accounts
 * Handles fetching and selecting accounts for trading
 */

import axiosInstance from '../api/axios';

class BrokerAccountService {
  constructor() {
    // Using axios instance instead of Firebase Functions SDK
  }

  /**
   * Fetch available accounts for a broker integration
   * @param {Object} params - Request parameters
   * @param {string} params.broker - Broker type (tradier, tastytrade, etc.)
   * @param {string} params.integrationId - Integration ID
   * @param {string} params.userId - User ID
   * @returns {Promise<Object>} Response with accounts array
   */
  async fetchAccounts({ broker, integrationId, userId }) {
    try {
      console.log('🔍 Fetching accounts for:', { broker, integrationId, userId });
      
      // Use axios instance which includes Firebase Auth token automatically
      const response = await axiosInstance.post('/fetch_broker_accounts', {
        broker,
        integrationId,
        userId
      });

      console.log('📥 Fetch accounts response:', response.data);

      if (response.data?.success) {
        return response.data;
      }

      throw new Error(response.data?.message || 'Failed to fetch accounts');
    } catch (error) {
      console.error('❌ Error fetching broker accounts:', error);
      // Include more error details for debugging
      if (error.response) {
        console.error('Response error:', error.response.data);
        throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to fetch accounts');
      }
      throw error;
    }
  }

  /**
   * Select an account for trading
   * @param {Object} params - Request parameters
   * @param {string} params.integrationId - Integration ID
   * @param {string} params.accountNumber - Selected account number
   * @param {string} params.userId - User ID
   * @returns {Promise<Object>} Response with success status
   */
  async selectAccount({ integrationId, accountNumber, userId }) {
    try {
      // Use unified axios instance for consistency
      const response = await axiosInstance.post('/broker_accounts', {
        integrationId,
        accountNumber,
        userId,
        action: 'select'
      });

      return response.data;
    } catch (error) {
      console.error('Error selecting account:', error);
      throw error;
    }
  }

  /**
   * Fetch account balances for a broker integration
   * @param {Object} params - Request parameters
   * @param {string} params.broker - Broker type (tradier, tastytrade, etc.)
   * @param {string} params.accountId - Account ID
   * @param {string} params.userId - User ID
   * @returns {Promise<Object>} Response with balance data
   */
  async fetchAccountBalances({ broker, accountId, userId }) {
    try {
      // Debug: verify required params before request
      console.log('🔎 brokerAccountService.fetchAccountBalances params:', {
        broker,
        accountId,
        userId,
      });
      if (!broker || !accountId || !userId) {
        console.warn('⚠️ Missing params for fetchAccountBalances. Skipping request.');
        throw new Error('Missing required parameters on client: broker, accountId, userId');
      }
      // Use axios instance which includes Firebase Auth token automatically
      const response = await axiosInstance.post('/fetch_account_balances', {
        broker,
        accountId,
        userId
      });

      // Debug: log raw response shape for field mapping
      try {
        console.log('📦 Balance API raw response:', JSON.stringify(response.data)?.slice(0, 2000));
      } catch (_) {
        console.log('📦 Balance API raw response (non-serializable):', response.data);
      }

      if (response.data?.success) {
        return response.data;
      }

      throw new Error(response.data?.message || 'Failed to fetch account balances');
    } catch (error) {
      console.error('❌ Error fetching account balances:', error);
      // Include more error details for debugging
      if (error.response) {
        console.error('Response error:', error.response.data);
        throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to fetch account balances');
      }
      throw error;
    }
  }

  /**
   * Validate account selection
   * @param {Object} account - Account object
   * @returns {boolean} True if valid
   */
  validateAccount(account) {
    if (!account) return false;
    
    const accountNumber = account.account_number || account.accountNumber;
    if (!accountNumber) return false;
    
    // Check if account is active
    if (account.status && account.status !== 'active') {
      console.warn('Account is not active:', account.status);
      return false;
    }
    
    return true;
  }

  /**
   * Format account display name
   * @param {Object} account - Account object
   * @returns {string} Formatted display name
   */
  formatAccountName(account) {
    if (!account) return 'Unknown Account';
    
    const accountNumber = account.account_number || account.accountNumber;
    const name = account.name || account.account_name;
    
    if (name) {
      return name + ' (' + accountNumber + ')';
    }
    
    return 'Account ' + accountNumber;
  }

  /**
   * Get account type badge info
   * @param {string} type - Account type
   * @returns {Object} Badge configuration
   */
  getAccountTypeBadge(type) {
    const badges = {
      'live': { color: 'green', label: 'Live' },
      'paper': { color: 'yellow', label: 'Paper' },
      'sandbox': { color: 'yellow', label: 'Sandbox' },
      'margin': { color: 'purple', label: 'Margin' },
      'cash': { color: 'blue', label: 'Cash' },
      'pdt': { color: 'orange', label: 'PDT' }
    };
    
    const lowerType = type ? type.toLowerCase() : null;
    return badges[lowerType] || { color: 'gray', label: type || 'Unknown' };
  }
}

// Export singleton instance
const brokerAccountService = new BrokerAccountService();
export default brokerAccountService;
